import { ClientAuthError } from "@azure/msal-browser";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import { User } from "types/type.user";
import {
  ElementType,
  Link,
  LinkId,
  Reference,
  ReferenceId,
  Term,
  TermId,
} from "types/types";
import { get, getWithHeaders } from "utils/api";
import {
  getTerms,
  getLinks,
  initializeUser,
  getReferences,
  postTerm,
  postLink,
  postRef,
  postModule,
  patchTerm,
  patchLink,
  patchRef,
  deleteTerm,
  deleteLink,
  deleteReference,
  fetchClientAndTenantApi,
} from "../Logic/ModuleApi";
import { JSONToArray } from "../Logic/RichTextTranslator";
import { validateLink } from "../Logic/utils";
import { RootState } from "./store";

export type UserData = {
  terms?: Record<TermId, Term>;
  links?: Record<LinkId, Link>;
  ref?: Record<ReferenceId, Reference>;
  data?: User;
};

export interface UserState {
  user: UserData;
  isMenuShowing: boolean;
  dataWasFetched: boolean;
  error: boolean;
  loading: boolean;
  loadingAnimation: boolean;
  newBeEvents: Record<string, { type: string; snapshot: string }>;
  clientId: string | null;
  tenantId: string | null;
}

const initialState: UserState = {
  user: {},
  isMenuShowing: false,
  dataWasFetched: false,
  error: false,
  loading: false,
  loadingAnimation: false,
  newBeEvents: {},
  clientId: null,
  tenantId: null,
};

const resolveElementKey = (type: ElementType): keyof UserState["user"] => {
  switch (type) {
    case ElementType.Term:
      return "terms";
    case ElementType.Link:
      return "links";
    case ElementType.Reference:
      return "ref";
  }
};

/*
  @function userSlice
  create user redux state, this will handle all the comunication with the backend
*/
export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setMenuShowing: (state) => {
      state.isMenuShowing = !state.isMenuShowing;
    },
    reset: () => initialState,
    setLoading: (state) => {
      state.loading = true;
    },
    setError: (state) => {
      state.error = false;
    },
    setLoadingAnimation: (state) => {
      state.loadingAnimation = true;
    },
    resetLoadingAnimation: (state) => {
      state.loadingAnimation = false;
    },
    changeElement: (
      state,
      {
        payload,
      }: PayloadAction<{
        type: ElementType;
        content: Record<string, string>;
        id: string;
      }>
    ) => {
      const { type, content, id } = payload;
      const elementKey = resolveElementKey(type);
      Object.keys(content).forEach((k) => {
        const target = (state.user[elementKey] as Record<string, any>)?.[id];
        if (target) {
          target[k] = content[k];
        }
      });
      if (state.newBeEvents[id] === undefined) {
        // TODO: which type should be used here?
        state.newBeEvents[id] = {
          // type: "patch",
          snapshot: JSON.stringify(state.user[elementKey]),
          type: type,
        };
      }
    },
    setElementText: (
      state,
      action: PayloadAction<{
        type: ElementType;
        id: string;
        value: string;
      }>
    ) => {
      const { id, type, value } = action.payload;
      if (type === ElementType.Term && state.user.terms) {
        state.user.terms[id].description = value;
      }
      if (type === ElementType.Reference && state.user.ref) {
        state.user.ref[id].description = value;
      }
      if (type === ElementType.Link && state.user.links) {
        state.user.links[id].description = value;
      }
    },
    setClientDetails: (
      state,
      {
        payload,
      }: PayloadAction<{ clientId: string | null; tenantId: string | null }>
    ) => {
      state.clientId = payload.clientId;
      state.tenantId = payload.tenantId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeUserByToken.fulfilled, (state, action) => {
        if ("error" in action.payload) {
          state.error = true;
          state.loading = false;
        } else {
          state.user.data = action.payload;
          state.loading = false;
          state.error = false;
          state.loadingAnimation = false;
        }
      })
      .addCase(pushNobocapDetailsToBackend.fulfilled, (state, action) => {
        if ("error" in action.payload) {
          state.error = true;
          state.loading = false;
        } else {
          state.user.data = action.payload;
          state.loading = false;
          state.error = false;
          state.loadingAnimation = false;
        }
      })
      .addCase(addUserElementToBackend.fulfilled, (state, action) => {
        if (!state.user || !action.payload) return;

        if (action.payload.type === ElementType.Term) {
          state.user = {
            ...state.user,
            terms: {
              ...state.user.terms,
              [action.payload.response.data.term_id]: {
                description: action.payload.response.data.description,
                name: action.payload.response.data.name,
                term_id: action.payload.response.data.term_id,
              },
            },
          };
        }
        if (action.payload.type === ElementType.Link) {
          state.user = {
            ...state.user,
            links: {
              ...state.user.links,
              [action.payload.response.data.link_id]: {
                description: action.payload.response.data.description,
                name: action.payload.response.data.name,
                address: action.payload.response.data.address,
                link_id: action.payload.response.data.link_id,
              },
            },
          };
        }
        if (action.payload.type === ElementType.Reference) {
          state.user = {
            ...state.user,
            ref: {
              ...state.user.ref,
              [action.payload.response.data.reference_id]: {
                description: action.payload.response.data.description,
                code: action.payload.response.data.code,
                link: action.payload.response.data.link,
                reference_id: action.payload.response.data.reference_id,
              },
            },
          };
        }
      })
      .addCase(changeUserElementToBackend.fulfilled, (state, { payload }) => {
        if (!payload.id || "error" in payload) return;

        const { [payload.id]: _, ...withoutRemoved } = state.newBeEvents;
        state.newBeEvents = withoutRemoved;
      })
      .addCase(loadUserData.fulfilled, (state, action) => {
        if ("error" in action.payload) {
          state.loading = false;
          state.loadingAnimation = false;
          state.error = true;
        } else {
          state.user = { ...state.user, ...action.payload };
          state.loading = false;
          state.loadingAnimation = false;
          state.error = false;
          state.dataWasFetched = true;
        }
      })
      .addCase(createModule.fulfilled, (state, action) => {
        if (!action.payload || "error" in action.payload) return;

        const { organizationId } = action.payload;
        if (state.user.data) {
          state.user.data.organisations.forEach((o) => {
            if (organizationId === o.organisation_id) {
              o.modules.push(action.payload.module);
            }
          });
        }
      })
      .addCase(deleteUserElementToBackend.fulfilled, (state, action) => {
        const type = action.payload.type;
        const id = action.payload.id;

        if (type === "Term" && state.user.terms && id) {
          delete state.user.terms[id];
        }
        if (action.payload.type === "Link" && state.user.links && id) {
          delete state.user.links[id];
        }
        if (action.payload.type === "Reference" && state.user.ref && id) {
          delete state.user.ref[id];
        }
      })
      .addCase(fetchClientAndTenant.fulfilled, (state, action) => {
        state.clientId = action.payload.client_id;
        state.tenantId = action.payload.tenant_id;
        state.loading = false;
        state.error = false;
        state.loadingAnimation = false;
      })
      .addCase(fetchClientAndTenant.rejected, (state) => {
        state.error = true;
      });
  },
});

/**
   @function createModule
   create module in backend
   @param orgId {string} - organization id
   @param name {string} - name of module
   @param is_live {boolean} - is module live
   @param is_free {boolean} - is module free
   @param dispaly_order {number} - display order of module
   @param next_module_id {string} - id of the next module
*/

export const createModule = createAsyncThunk(
  "producer/createModule",
  async ({
    orgId,
    name,
    isLive = false,
    isFree = false,
    displayOrder,
    nextModuleId,
  }: {
    orgId: string;
    name: string;
    isLive?: boolean;
    isFree?: boolean;
    displayOrder?: number;
    nextModuleId?: string;
  }) => {
    try {
      const response = await postModule({
        orgId,
        name,
        isLive,
        isFree,
        displayOrder,
        nextModuleId,
      });

      return { module: response, organizationId: orgId };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

export const initializeUserByToken = createAsyncThunk(
  "user/initializeUserByToken",
  async ({ invitationCode }: { invitationCode?: string | null }) => {
    try {
      const profile = await initializeUser({ invitationCode });
      if (!profile) {
        throw "error";
      }
      return profile;
    } catch (error) {
      return { error: true };
    }
  }
);

/**
   @function addTermToBackend
   add new term
   @param orgId {string} - organization id
   @param name {string} - name to save the chapter with 
   @param description {string} - description of term
*/
export const addUserElementToBackend = createAsyncThunk(
  "producer/addUserElement",
  async ({
    orgId,
    elementData,
    type,
    callback,
  }: {
    orgId: string;
    elementData: Record<string, any>;
    type: ElementType;
    callback?: (
      id: TermId | ReferenceId | LinkId,
      data?: Term | Reference | Link
    ) => void;
  }) => {
    try {
      if (type === ElementType.Term) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;

        const response: { data: Term } = await postTerm({
          orgId,
          name: elementData.name,
          description: translatedContent,
        });

        if (callback) {
          callback(response.data.term_id);
        }
        return {
          type,
          callback,
          response,
        };
      }
      if (type === ElementType.Link) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;
        const response = await postLink({
          orgId,
          name: elementData.name,
          description: translatedContent,
          address: elementData.address,
        });
        if (callback) {
          callback(response.data.link_id, response.data);
        }
        return {
          type,
          callback,
          response,
        };
      }
      if (type === ElementType.Reference) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;
        const response = await postRef({
          orgId,
          code: elementData.code,
          description: translatedContent,
          link: elementData.link,
        });

        if (callback) {
          callback(response.data.reference_id, response.data);
        }
        return {
          type,
          callback,
          response,
        };
      }
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function deleteUserElementToBackend
   delete user element
   @param orgId {string} - organization id
   @param idd {string} -  id
*/
export const deleteUserElementToBackend = createAsyncThunk(
  "producer/deleteUserElement",
  async ({
    orgId,
    type,
    element,
  }: {
    orgId: string;
    type: ElementType;
    element: Term | Reference | Link;
  }) => {
    try {
      let id = "";
      if (type === ElementType.Term && "term_id" in element) {
        id = element.term_id;

        await deleteTerm({
          orgId,
          id: element.term_id,
        });
      }
      if (type === ElementType.Link && "link_id" in element) {
        id = element.link_id;
        await deleteLink({
          orgId,
          id: element.link_id,
        });
      }
      if (type === ElementType.Reference && "reference_id" in element) {
        id = element.reference_id;
        await deleteReference({
          orgId,
          id: element.reference_id,
        });
      }

      return {
        type: type,
        id: id,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function changeUserElementToBackend
   change element
   @param orgId {string} - organization id
   @param elementData {object} - element data 
*/
export const changeUserElementToBackend = createAsyncThunk(
  "producer/changeUserElement",
  async ({
    orgId,
    type,
    id,
    elementData,
  }: {
    orgId: string;
    type?: ElementType;
    id: TermId | LinkId | ReferenceId;
    elementData: Record<string, any>;
  }) => {
    try {
      if (type === ElementType.Term) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;

        await patchTerm({
          orgId,
          name: elementData.name,
          description: translatedContent,
          id,
        });
      } else if (type === ElementType.Link) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;

        if (!validateLink(elementData.address)) {
          return {};
        }
        await patchLink({
          orgId,
          name: elementData.name,
          description: translatedContent,
          address: elementData.address,
          id,
        });
      } else if (type === ElementType.Reference) {
        const translatedContent =
          elementData.description.content !== undefined
            ? {
              text: JSONToArray(elementData.description.content),
            }
            : elementData.description;
        if (!validateLink(elementData.link)) {
          return {};
        }
        await patchRef({
          orgId,
          code: elementData.code,
          description: translatedContent,
          link: elementData.link,
          id,
        });
      }

      return {
        id,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

export const loadUserData = createAsyncThunk(
  "user/loadUserData",
  async ({ organisationIds }: { organisationIds: string[] }) => {
    try {
      // TODO: use reduce instead of mutating data
      let terms: Record<string, Term> = {};
      let links: Record<string, Link> = {};
      let ref: Record<string, Reference> = {};
      await Promise.all(
        organisationIds.map(async (organisation) => {
          await getTerms({
            orgId: organisation,
            isUpdated: true,
          }).then((term) => {
            terms = { ...terms, ...term };
          });
          await getLinks({
            orgId: organisation,
            isUpdated: true,
          }).then((link) => {
            links = { ...links, ...link };
          });
          await getReferences({
            orgId: organisation,
            isUpdated: true,
          }).then((reference) => {
            ref = { ...ref, ...reference };
          });
        })
      );

      return { ...{ terms: terms }, ...{ links: links }, ...{ ref: ref } };
    } catch (error) {
      return { error: true };
    }
  }
);

export const fetchClientAndTenant = createAsyncThunk(
  "user/identity_provider",
  async (
    {
      email,
      identity_provider_type,
    }: {
      email: string;
      identity_provider_type: string;
    },
    thunkApi
  ) => {
    try {
      const details = await fetchClientAndTenantApi(
        email,
        identity_provider_type
      );
      if (!details) {
        throw "error";
      }
      return details;
    } catch (error) {
      return thunkApi.rejectWithValue({ error: true });
    }
  }
);

export const pushNobocapDetailsToBackend = createAsyncThunk("app/user/profile/nobocap",
  async ({ data, token }: any, thunkApi) => {
    try {
      const { email } = data;
      const response = await get("/user/profile", undefined);
      return  response.json().then((j) => j.data);
    }
    catch (error) {
      return { error: true }
    }
  });

export const {
  setMenuShowing,
  reset,
  setLoading,
  setError,
  setLoadingAnimation,
  changeElement,
  setElementText,
  setClientDetails,
  resetLoadingAnimation
} = userSlice.actions;

export default userSlice.reducer;

const selectState = (state: RootState) => state.user;
export const selectUser = createSelector(selectState, ({ user }) => user);

export const selectUserData = createSelector(selectUser, (user) => user.data);

export const selectHasUserData = createSelector(
  selectUserData,
  (data) => data !== undefined
);

export const selectOrganizations = createSelector(
  selectUserData,
  (data) => data?.organisations || []
);

export const selectDataWasFetched = createSelector(
  selectState,
  ({ dataWasFetched }) => dataWasFetched
);

export const selectIsMenuShowing = createSelector(
  selectState,
  ({ isMenuShowing }) => isMenuShowing
);

export const selectUserTerms = createSelector(selectUser, ({ terms }) => terms);

export const selectUserRefs = createSelector(selectUser, ({ ref }) => ref);

export const selectUserLinks = createSelector(selectUser, ({ links }) => links);

export const selectClientId = createSelector(
  selectState,
  ({ clientId }) => clientId
);

export const selectTenantId = createSelector(
  selectState,
  ({ tenantId }) => tenantId
);

export const selectLoading = createSelector(
  selectState,
  ({ loading }) => loading
);
export const selectLoadingAnimation = createSelector(
  selectState,
  ({ loadingAnimation }) => loadingAnimation
);
export const selectError = createSelector(selectState, ({ error }) => error);

export const selectNewBeEvents = createSelector(
  selectState,
  ({ newBeEvents }) => newBeEvents
);

export const selectUserId = createSelector(
  selectUserData,
  (userData) => userData?.user_id
);

export const selectUserInvitations = createSelector(
  selectUserData,
  (userData) => userData?.user_invitations || []
);

const selectHasInvitations = createSelector(
  selectUserInvitations,
  (invitations) => invitations.length !== 0
);

export const selectIsNewUserPendingVerification = createSelector(
  selectHasInvitations,
  selectOrganizations,
  (hasInvitations, organizations) =>
    hasInvitations && organizations.length === 0
);

export const selectIsPendingVerificationAndHasOrganizations = createSelector(
  selectHasInvitations,
  selectOrganizations,
  (hasInvitations, organizations) => hasInvitations && organizations.length > 0
);
